/**
 * Word Tetris Game Utilities
 * Tetromino blocks with words from your threads
 */

// Grid constants
export const GRID_WIDTH = 10;
export const GRID_HEIGHT = 20;
export const CELL_SIZE = 28;

// Tetromino shapes (standard 7 pieces)
// Each shape is an array of rotations, each rotation is [row][col]
export const TETROMINOES = {
  I: {
    shape: [
      [[1, 1, 1, 1]],
      [[1], [1], [1], [1]],
    ],
    color: 'cyan',
  },
  O: {
    shape: [
      [[1, 1], [1, 1]],
    ],
    color: 'yellow',
  },
  T: {
    shape: [
      [[0, 1, 0], [1, 1, 1]],
      [[1, 0], [1, 1], [1, 0]],
      [[1, 1, 1], [0, 1, 0]],
      [[0, 1], [1, 1], [0, 1]],
    ],
    color: 'purple',
  },
  L: {
    shape: [
      [[0, 0, 1], [1, 1, 1]],
      [[1, 0], [1, 0], [1, 1]],
      [[1, 1, 1], [1, 0, 0]],
      [[1, 1], [0, 1], [0, 1]],
    ],
    color: 'orange',
  },
  J: {
    shape: [
      [[1, 0, 0], [1, 1, 1]],
      [[1, 1], [1, 0], [1, 0]],
      [[1, 1, 1], [0, 0, 1]],
      [[0, 1], [0, 1], [1, 1]],
    ],
    color: 'blue',
  },
  S: {
    shape: [
      [[0, 1, 1], [1, 1, 0]],
      [[1, 0], [1, 1], [0, 1]],
    ],
    color: 'green',
  },
  Z: {
    shape: [
      [[1, 1, 0], [0, 1, 1]],
      [[0, 1], [1, 1], [1, 0]],
    ],
    color: 'red',
  },
};

// Color palette for blocks (matches design system)
export const BLOCK_COLORS = {
  cyan: { fill: '#279c9c', stroke: '#167b7d', text: '#ffffff' },
  yellow: { fill: '#FFC933', stroke: '#CC9600', text: '#332500' },
  purple: { fill: '#8f6cc9', stroke: '#6b4fa8', text: '#ffffff' },
  orange: { fill: '#fc9162', stroke: '#bf4815', text: '#ffffff' },
  blue: { fill: '#5c9cf5', stroke: '#3d7dd6', text: '#ffffff' },
  green: { fill: '#43c478', stroke: '#077d55', text: '#ffffff' },
  red: { fill: '#fa5343', stroke: '#d91f11', text: '#ffffff' },
};

/**
 * Extract words from text (Korean + English support)
 */
export const extractWords = (text) => {
  if (!text) return [];
  // Split by whitespace and filter empty
  const words = text.split(/\s+/).filter(w => w.length > 0);
  // Remove URLs, mentions, hashtags for cleaner words
  return words.filter(w => !w.startsWith('http') && !w.startsWith('@'));
};

/**
 * Filter threads with minimum word count
 */
export const filterLongThreads = (threads, minWords = 30) => {
  if (!Array.isArray(threads)) return [];
  return threads.filter(thread => {
    const words = extractWords(thread?.text || '');
    return words.length >= minWords;
  });
};

/**
 * Select random thread
 */
export const selectRandomThread = (threads) => {
  if (!threads?.length) return null;
  return threads[Math.floor(Math.random() * threads.length)];
};

/**
 * Word Queue - manages words to display on blocks
 */
export class WordQueue {
  constructor(words) {
    this.words = [...words];
    this.index = 0;
  }

  getNext() {
    if (this.index >= this.words.length) {
      this.index = 0; // Loop back
    }
    return this.words[this.index++] || '';
  }

  reset() {
    this.index = 0;
  }
}

/**
 * Create empty grid
 */
export const createEmptyGrid = () => {
  return Array(GRID_HEIGHT).fill(null).map(() => Array(GRID_WIDTH).fill(null));
};

/**
 * Get random tetromino type
 */
export const getRandomTetromino = () => {
  const types = Object.keys(TETROMINOES);
  return types[Math.floor(Math.random() * types.length)];
};

/**
 * Create a new piece
 */
export const createPiece = (type, word) => {
  const tetromino = TETROMINOES[type];
  const shape = tetromino.shape[0];
  const width = shape[0].length;

  return {
    type,
    word: truncateWord(word, 4), // Max 4 chars for display
    fullWord: word,
    shape,
    rotation: 0,
    x: Math.floor((GRID_WIDTH - width) / 2),
    y: 0,
    color: tetromino.color,
  };
};

/**
 * Truncate word for block display
 */
const truncateWord = (word, maxLen) => {
  if (!word) return '';
  if (word.length <= maxLen) return word;
  return word.slice(0, maxLen);
};

/**
 * Rotate piece
 */
export const rotatePiece = (piece) => {
  const tetromino = TETROMINOES[piece.type];
  const nextRotation = (piece.rotation + 1) % tetromino.shape.length;
  return {
    ...piece,
    shape: tetromino.shape[nextRotation],
    rotation: nextRotation,
  };
};

/**
 * Check if position is valid
 */
export const isValidPosition = (grid, piece, offsetX = 0, offsetY = 0) => {
  const { shape, x, y } = piece;
  for (let row = 0; row < shape.length; row++) {
    for (let col = 0; col < shape[row].length; col++) {
      if (shape[row][col]) {
        const newX = x + col + offsetX;
        const newY = y + row + offsetY;

        if (newX < 0 || newX >= GRID_WIDTH) return false;
        if (newY >= GRID_HEIGHT) return false;
        if (newY >= 0 && grid[newY][newX]) return false;
      }
    }
  }
  return true;
};

/**
 * Lock piece to grid
 */
export const lockPiece = (grid, piece) => {
  const newGrid = grid.map(row => [...row]);
  const { shape, x, y, color, word, fullWord } = piece;

  for (let row = 0; row < shape.length; row++) {
    for (let col = 0; col < shape[row].length; col++) {
      if (shape[row][col] && y + row >= 0) {
        newGrid[y + row][x + col] = { color, word, fullWord };
      }
    }
  }
  return newGrid;
};

/**
 * Clear completed lines and return info
 */
export const clearLines = (grid) => {
  const clearedWords = [];
  let linesCleared = 0;

  const newGrid = grid.filter((row, idx) => {
    const isFull = row.every(cell => cell !== null);
    if (isFull) {
      linesCleared++;
      row.forEach(cell => {
        if (cell?.fullWord) clearedWords.push(cell.fullWord);
      });
      return false;
    }
    return true;
  });

  // Add empty rows at top
  while (newGrid.length < GRID_HEIGHT) {
    newGrid.unshift(Array(GRID_WIDTH).fill(null));
  }

  return { grid: newGrid, linesCleared, clearedWords };
};

/**
 * Calculate score
 */
export const calculateScore = (lines, level) => {
  const points = [0, 100, 300, 500, 800];
  return (points[lines] || lines * 200) * level;
};

/**
 * Calculate level
 */
export const calculateLevel = (totalLines) => {
  return Math.min(15, Math.floor(totalLines / 10) + 1);
};

/**
 * Calculate drop interval (ms)
 */
export const calculateDropInterval = (level) => {
  return Math.max(100, 1000 - (level - 1) * 60);
};
